import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuFolderKanban, LuHospital } from 'react-icons/lu';
import Modal from '../../components/ui/Modal';
import GlassIcon from '../../components/ui/GlassIcon';
import Badge from '../../components/ui/Badge';
import { useTranslation } from '../../i18n/LanguageContext';
import { formatDate, formatRate } from './projectHelpers';
import ProjectPatientSummary from './ProjectPatientSummary';

const cellClass = 'tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-slate-700 dark:tw-text-slate-200';
const headClass = 'tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400';

/** One project of one employee: project facts, their centers/roles and patient summary. */
const ProjectDetailModal = ({ project, assignments, employee, isAdmin, onClose }) => {
  const { t, locale, language } = useTranslation();

  // Çalışanın bu projede görevli olduğu merkezler
  const centerRows = useMemo(() => {
    const rows = new Map();
    assignments.forEach((assignment) => {
      const key = assignment.centerId || assignment.centerName || '-';
      const existing = rows.get(key);
      if (existing) {
        if (assignment.role && !existing.roles.includes(assignment.role)) {
          existing.roles.push(assignment.role);
        }
        if (assignment.status !== 'passive') existing.status = 'active';
      } else {
        rows.set(key, {
          key,
          centerName: assignment.centerName || null,
          roles: assignment.role ? [assignment.role] : [],
          workerType: assignment.workerType || '-',
          blindStatus: assignment.blindStatus || '-',
          assignedAt: assignment.assignedAt,
          rate: assignment.rate,
          status: assignment.status === 'passive' ? 'passive' : 'active',
        });
      }
    });
    return Array.from(rows.values());
  }, [assignments]);

  const hasActiveAssignment = centerRows.some((row) => row.status === 'active');
  const projectActive = (project.projectStatus || 'Aktif') === 'Aktif';
  const statusBadge = (active) => (
    <Badge tone={active ? 'success' : 'neutral'}>{active ? t('common.active') : t('common.passive')}</Badge>
  );

  const facts = [
    { label: t('projectDetail.protocol'), value: project.protocolNumber || '-' },
    { label: t('projectDetail.customer'), value: project.customer || '-' },
    { label: t('projectDetail.startDate'), value: formatDate(project.projectDate, locale) },
    { label: t('projectDetail.endDate'), value: formatDate(project.projectDueDate, locale) },
    { label: t('projectDetail.projectStatus'), value: statusBadge(projectActive) },
    { label: t('projectDetail.assignmentStatus'), value: statusBadge(hasActiveAssignment) },
  ];

  const columns = ['center', 'role', 'workerType', 'blinding', 'assignedAt', ...(isAdmin ? ['rate'] : []), 'status'];
  const cells = (row) => ({
    center: row.centerName || t('projectDetail.unspecified'),
    role: row.roles.length ? row.roles.join(', ') : '-',
    workerType: row.workerType,
    blinding: row.blindStatus,
    assignedAt: formatDate(row.assignedAt, locale),
    rate: formatRate(row.rate, t('projects.hourly')),
    status: statusBadge(row.status === 'active'),
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      lang={language}
      title={project.titleShort || t('projectDetail.fallbackTitle')}
      subtitle={project.titleLong || '-'}
      icon={(
        <GlassIcon gradient="tw-from-violet-500 tw-to-fuchsia-500 tw-shadow-violet-500/30">
          <LuFolderKanban aria-hidden="true" className="tw-h-6 tw-w-6" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
    >
      <div className="tw-space-y-6">
        <dl className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3">
          {facts.map((fact) => (
            <div key={fact.label} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50 tw-px-4 tw-py-3 dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
              <dt className="tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{fact.label}</dt>
              <dd className="tw-mt-1 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{fact.value}</dd>
            </div>
          ))}
        </dl>

        <section>
          <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
            <LuHospital aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-500" />
            {t('projectDetail.centersTitle')}
            <Badge tone="neutral">{centerRows.length}</Badge>
          </h3>

          {centerRows.length === 0 ? (
            <p className="tw-mt-3 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{t('projectDetail.noCenters')}</p>
          ) : (
            <>
              {/* Phones: one card per center */}
              <ul className="tw-mt-3 tw-space-y-2 md:tw-hidden">
                {centerRows.map((row) => {
                  const values = cells(row);
                  return (
                    <li key={row.key} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-p-4 dark:tw-border-white/10">
                      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                        <p className="tw-min-w-0 tw-font-semibold tw-text-slate-900 dark:tw-text-white">{values.center}</p>
                        {values.status}
                      </div>
                      <dl className="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-text-xs">
                        {columns.filter((col) => col !== 'center' && col !== 'status').map((col) => (
                          <div key={col} className="tw-min-w-0">
                            <dt className="tw-text-slate-500 dark:tw-text-slate-400">{t(`projectDetail.columns.${col}`)}</dt>
                            <dd className="tw-m-0 tw-truncate tw-font-medium tw-text-slate-700 dark:tw-text-slate-200">{values[col]}</dd>
                          </div>
                        ))}
                      </dl>
                    </li>
                  );
                })}
              </ul>

              {/* Tablet and up: table */}
              <div className="tw-mt-3 tw-hidden tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar md:tw-block dark:tw-border-white/10">
                <table className="tw-w-full tw-border-collapse tw-text-sm">
                  <thead className="tw-bg-slate-50 dark:tw-bg-white/[0.04]">
                    <tr className="tw-bg-transparent">
                      {columns.map((col) => (
                        <th key={col} className={headClass}>{t(`projectDetail.columns.${col}`)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="tw-divide-y tw-divide-slate-200/80 dark:tw-divide-white/10">
                    {centerRows.map((row) => {
                      const values = cells(row);
                      return (
                        <tr key={row.key} className="tw-bg-transparent tw-transition-colors hover:tw-bg-slate-50/70 dark:hover:tw-bg-white/[0.03]">
                          {columns.map((col) => (
                            <td key={col} className={cellClass}>{values[col]}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        <ProjectPatientSummary projectId={project.id} employeeId={employee.id} />
      </div>
    </Modal>
  );
};

ProjectDetailModal.propTypes = {
  project: PropTypes.object.isRequired,
  assignments: PropTypes.arrayOf(PropTypes.object).isRequired,
  employee: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default ProjectDetailModal;
